import classNames from 'classnames';
import type { AriaRole, ReactNode } from 'react';
import React, { forwardRef } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import * as styles from './ToolbarButton.module.scss';
import Caret from '../../../../components/Caret';
import SVGIcon from '../../../../components/SVGIcon';
import Tooltip from '../../../../components/Tooltip';
import addTestId from '../../../../utils/addTestId';
import ClickOutsideHandler from '../../../../utils/ClickOutsideHandler';
import Link from '../../../../utils/router/Link';
import normalizeShortcut from '../../../utils/normalizeShortcut';

// Align the pop-over horizontally with the button, compensating for the border
const GROUP_BORDER_COMPENSATION = 1;

interface ToolbarButtonGroupProps {
  'aria-label'?: string;
  role?: AriaRole;
  children: ReactNode;
}

export const ToolbarButtonGroup = forwardRef<
  HTMLDivElement,
  ToolbarButtonGroupProps
>(({ 'aria-label': ariaLabel, role, children }, ref) => (
  <div ref={ref} role={role} aria-label={ariaLabel} className={styles.group}>
    {children}
  </div>
));

type ToolbarButtonProps = {
  icon?: ReactNode;
  active?: boolean;
  disabled?: boolean;
  renderPopOver?: (onDismiss: () => void) => ReactNode;
  popOverHideCaret?: boolean;
  popOverOpen?: boolean;
  onPopOverOpenChange?: (open: boolean) => void;
  rightAlignPopOver?: boolean;
  children?: ReactNode;
  onClick?: () => void;
  to?: string;
  testId?: string;
  accessibleType?: 'toggle' | 'radio';
  'aria-label'?: string;
  newTab?: React.ComponentProps<typeof Link>['newTab'];
} & ({ tooltip?: never } | { tooltip: string; shortcut?: string });

const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (
    {
      icon,
      accessibleType,
      active = false,
      disabled,
      renderPopOver,
      popOverHideCaret,
      popOverOpen,
      onPopOverOpenChange,
      rightAlignPopOver,
      children,
      onClick,
      to,
      testId,
      'aria-label': ariaLabel,
      newTab,
      ...props
    }: ToolbarButtonProps,
    ref,
  ) => {
    const caret =
      renderPopOver && !popOverHideCaret && (popOverOpen ? 'up' : 'down');
    const iconNode = icon && (
      <span aria-hidden className={styles.icon}>
        {typeof icon === 'string' ? (
          <SVGIcon inline size="small">
            {icon}
          </SVGIcon>
        ) : (
          icon
        )}
      </span>
    );

    let button = (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (onClick) {
            onClick();
          }
          if (renderPopOver) {
            onPopOverOpenChange?.(!popOverOpen);
          }
        }}
        className={classNames(styles.button, {
          [styles.active]: active,
          [styles.selected]: renderPopOver && popOverOpen,
        })}
        {...addTestId(testId)}
        aria-checked={accessibleType === 'radio' ? active : undefined}
        aria-pressed={accessibleType === 'toggle' ? active : undefined}
        role={accessibleType === 'radio' ? 'radio' : undefined}
        aria-label={
          ariaLabel ||
          (typeof props.tooltip === 'string'
            ? props.tooltip.replace(/\n/g, ' ')
            : undefined)
        }
      >
        {iconNode}
        {!!children && <div className={styles.label}>{children}</div>}
        {caret && (
          <div className={styles.caret}>
            <Caret direction={caret} />
          </div>
        )}
      </button>
    );

    if (to) {
      button = (
        <Link className={styles.link} to={to} newTab={newTab}>
          {button}
        </Link>
      );
    }

    if (props.tooltip) {
      button = (
        <Tooltip
          display="block"
          content={
            <>
              {props.tooltip}
              {props.shortcut && (
                <>
                  <br />
                  <Tooltip.Muted className={styles.shortcut}>
                    {normalizeShortcut(props.shortcut)}
                  </Tooltip.Muted>
                </>
              )}
            </>
          }
          offset={2}
        >
          {button}
        </Tooltip>
      );
    }

    return (
      <div className={styles.container}>
        {renderPopOver ? (
          <ClickOutsideHandler
            onClickOutside={() => popOverOpen && onPopOverOpenChange?.(false)}
          >
            <Manager>
              <Reference>
                {({ ref: referenceRef }) => (
                  <div ref={referenceRef}>{button}</div>
                )}
              </Reference>
              {popOverOpen && (
                <Popper
                  placement={rightAlignPopOver ? 'bottom-end' : 'bottom-start'}
                  modifiers={[
                    {
                      name: 'offset',
                      options: {
                        offset: [
                          rightAlignPopOver
                            ? GROUP_BORDER_COMPENSATION
                            : -GROUP_BORDER_COMPENSATION,
                          5,
                        ],
                      },
                    },
                  ]}
                >
                  {({ ref: popOverRef, style }) => (
                    <div
                      className={styles.popOver}
                      ref={popOverRef}
                      style={style}
                    >
                      <div className={styles.container}>
                        {renderPopOver(() => {
                          onPopOverOpenChange?.(false);
                        })}
                      </div>
                    </div>
                  )}
                </Popper>
              )}
            </Manager>
          </ClickOutsideHandler>
        ) : (
          button
        )}
      </div>
    );
  },
);

export default ToolbarButton;
